import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useState } from "react"
import { Search, User, X, Briefcase, Check, RotateCw } from "lucide-react"
import { HeroVorgangName } from "./HeroVorgangName"
import { VORGANG_OHNE_NAME, vorgangBezeichnung } from "@/lib/vorgangName"
import { cn } from "@/lib/utils"
import { toast } from "./Toast"
import { kontaktName } from "@/lib/heroSearch"
import { wegSchritt } from "@/lib/wege"

// HERO-Link-Modal — Kontakt (und optional Projekt) einer E-Mail / einem
// Anruf zuordnen.
//
// Nutzt die bestehenden /admin/<bereich>/api/-Endpoints direkt (Session-
// Cookie gleiche Domain). Keine Business-Logik dupliziert.
//
// Modi:
//   "email"   → /admin/emails/api/...
//   "sipgate" → /admin/sipgate/api/...
//   "meiti"   → /admin/calls/api/...
type Mode = "email" | "sipgate" | "meiti"

export interface HeroContact {
  id: number
  first_name: string
  last_name: string
  /** Bei reinen Firmenkontakten der einzige Name — first/last sind leer (#52). */
  company_name: string
  email: string
  phone: string
  address: string
}

export interface HeroProject {
  id: number
  name: string
  code?: string
  contact_name?: string
  contact_email?: string
  status?: string
}

// Email-Mode auf v1 PATCH /hero-link konsolidiert (Welle 2). Sipgate/Meiti
// bleiben in Welle 5/6.
const ENDPOINTS = {
  email: {
    search: "/api/v1/contacts/search",
    // v1 nutzt PATCH /hero-link mit unterschiedlichen Bodies — die alten
    // Pfade unten zeigen alle auf den gleichen v1-Endpoint, aber das Frontend
    // muss bei Setzen/Clearen unterschiedlichen Body schicken (siehe Aufrufer).
    setContact: (id: number) => `/api/v1/emails/${id}/hero-link`,
    searchProjects: "/api/v1/projects/search",
    assignProject: (id: number) => `/api/v1/emails/${id}/hero-link`,
    clearProject: (id: number) => `/api/v1/emails/${id}/hero-link`,
    heroProjects: (id: number) => `/api/v1/emails/${id}/hero-projects`,
    clearAll: (id: number) => `/api/v1/emails/${id}/hero-link`,
  },
  sipgate: {
    // Konsolidiert auf den v1-PATCH /hero-link (wie meiti). Die alten
    // /admin/sipgate/...-Routen wurden beim Cleanup entfernt → Verknüpfen lief
    // ins Leere (Bugfix 15.06.2026).
    search: "/api/v1/contacts/search",
    setContact: (id: number) => `/api/v1/sipgate/${id}/hero-link`,
    searchProjects: "/api/v1/projects/search",
    assignProject: (id: number) => `/api/v1/sipgate/${id}/hero-link`,
    clearProject: (id: number) => `/api/v1/sipgate/${id}/hero-link`,
    heroProjects: (id: number) => `/api/v1/sipgate/${id}/hero-projects`,
    clearAll: (id: number) => `/api/v1/sipgate/${id}/hero-link`,
  },
  meiti: {
    search: "/api/v1/contacts/search",
    setContact: (id: number) => `/api/v1/calls/${id}/hero-link`,
    searchProjects: "/api/v1/projects/search",
    assignProject: (id: number) => `/api/v1/calls/${id}/hero-link`,
    clearProject: (id: number) => `/api/v1/calls/${id}/hero-link`,
    heroProjects: (id: number) => `/api/v1/calls/${id}/hero-projects`,
    clearAll: (id: number) => `/api/v1/calls/${id}/hero-link`,
  },
} as const

async function getJson<T = any>(url: string): Promise<T> {
  const res = await fetch(url, { credentials: "include" })
  if (!res.ok) throw new Error(`HTTP ${res.status}`)
  return res.json()
}

export function HeroLinkModal({
  open,
  onClose,
  mode,
  itemId,
  tenantSlug,
  currentContactId,
  currentContactName,
  currentProjectId,
  invalidateKeys,
}: {
  open: boolean
  onClose: () => void
  mode: Mode
  itemId: number
  tenantSlug: string
  currentContactId?: number | null
  currentContactName?: string | null
  currentProjectId?: number | string | null
  invalidateKeys: (string | number)[][]
}) {
  const qc = useQueryClient()
  const eps = ENDPOINTS[mode]

  const [search, setSearch] = useState(currentContactName || "")
  const [searched, setSearched] = useState(false)
  const [foundContacts, setFoundContacts] = useState<HeroContact[]>([])
  const [selectedContact] = useState<HeroContact | null>(null)
  const [projectSearch, setProjectSearch] = useState("")
  const [foundProjects, setFoundProjects] = useState<HeroProject[]>([])
  const [err, setErr] = useState<string | null>(null)

  const invalidate = () => invalidateKeys.forEach((k) => qc.invalidateQueries({ queryKey: k }))

  // Bestehende Projekte für aktuellen Kontakt anzeigen (wenn nicht neu am Suchen)
  const existingProjects = useQuery({
    queryKey: [mode, "hero-projects", itemId],
    queryFn: () => getJson<{
      projects: HeroProject[]
      parent?: { id: number; name: string; ansprechpartner_name?: string } | null
    }>(eps.heroProjects(itemId)),
    enabled: open && !!currentContactId && !selectedContact,
    staleTime: 1000 * 60,
  })

  // Der verknuepfte Vorgang, falls er unter den Projekten des Kontakts steht —
  // dann braucht die Anzeige keinen zweiten Lookup.
  const verknuepfterVorgang = existingProjects.data?.projects?.find(
    (p) => String(p.id) === String(currentProjectId),
  )

  // Prüft ob der currentContactId wirklich in HERO existiert (lokaler Cache + HERO-Fallback)
  const contactExists = useQuery({
    queryKey: ["contact-exists", currentContactId, tenantSlug],
    queryFn: async () => {
      const url = `/admin/contacts/api/${currentContactId}${tenantSlug ? `?tenant=${encodeURIComponent(tenantSlug)}` : ""}`
      const res = await fetch(url, { credentials: "include" })
      if (!res.ok) return { exists: false }
      const data = await res.json()
      if (data.error || !data.id) return { exists: false }
      return { exists: true, name: `${data.first_name || ""} ${data.last_name || ""}`.trim() }
    },
    enabled: open && !!currentContactId,
    staleTime: 1000 * 30,
  })

  const doContactSearch = useMutation({
    mutationFn: async () => {
      const url = `${eps.search}?tenant=${encodeURIComponent(tenantSlug)}&q=${encodeURIComponent(search)}`
      return getJson<{ items: HeroContact[]; total: number }>(url)
    },
    onSuccess: (d) => {
      setSearched(true)
      setFoundContacts(d.items || [])
    },
    onError: (e) => setErr(e instanceof Error ? e.message : String(e)),
  })

  const setContactMut = useMutation({
    mutationFn: async (c: HeroContact) => {
      // Alle Modi nutzen jetzt den konsolidierten v1-PATCH /hero-link mit
      // {contact_id, contact_name}. (isV1 bleibt als Schalter, falls künftig
      // wieder ein Modus mit Alt-Schema dazukommt.)
      const isV1 = mode === "email" || mode === "meiti" || mode === "sipgate"
      const body = isV1
        ? { contact_id: c.id, contact_name: kontaktName(c) }
        : { hero_contact_id: c.id, hero_contact_name: kontaktName(c) }
      const res = await fetch(eps.setContact(itemId), {
        method: isV1 ? "PATCH" : "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(body),
      })
      if (!res.ok) {
        let msg = `HTTP ${res.status}`
        try { const j = await res.json(); msg = j.error || j.detail || msg } catch { /* */ }
        throw new Error(msg)
      }
      return res.json()
    },
    onSuccess: () => { wegSchritt("kontakt_verknuepft"); invalidate(); toast("✓ HERO-Kontakt verknüpft"); onClose() },
    onError: (e) => setErr(e instanceof Error ? e.message : String(e)),
  })

  const doProjectSearch = useMutation({
    mutationFn: async () => {
      const url = `${eps.searchProjects}?tenant=${encodeURIComponent(tenantSlug)}&q=${encodeURIComponent(projectSearch)}`
      return getJson<{ items: HeroProject[]; total: number }>(url)
    },
    onSuccess: (d) => setFoundProjects(d.items || []),
    onError: (e) => setErr(e instanceof Error ? e.message : String(e)),
  })

  const assignProjectMut = useMutation({
    mutationFn: async (projectId: number) => {
      const isV1 = mode === "email" || mode === "meiti" || mode === "sipgate"
      const body = isV1 ? { project_id: projectId } : { hero_project_id: projectId }
      const res = await fetch(eps.assignProject(itemId), {
        method: isV1 ? "PATCH" : "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(body),
      })
      if (!res.ok) {
        let msg = `HTTP ${res.status}`
        try { const j = await res.json(); msg = j.error || j.detail || msg } catch { /* */ }
        throw new Error(msg)
      }
      return res.json()
    },
    onSuccess: () => { wegSchritt("vorgang_verknuepft"); invalidate(); toast("✓ HERO-Projekt verknüpft"); onClose() },
    onError: (e) => {
      const msg = e instanceof Error ? e.message : String(e)
      setErr(msg)
      toast("Projekt-Verknüpfung fehlgeschlagen: " + msg, "error")
    },
  })

  const clearMut = useMutation({
    mutationFn: async () => {
      const isV1 = mode === "email" || mode === "meiti" || mode === "sipgate"
      // v1: PATCH mit {contact_id: null, project_id: null} loescht beides
      const res = await fetch(eps.clearAll(itemId), {
        method: isV1 ? "PATCH" : "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: isV1 ? JSON.stringify({ contact_id: null, project_id: null }) : "{}",
      })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      return res.json()
    },
    onSuccess: () => { invalidate(); toast("✓ HERO-Verknüpfung entfernt"); onClose() },
  })

  if (!open) return null

  return (
    <div
      className="pp-overlay"
      data-portal-modal
      style={{ position: "fixed", inset: 0, zIndex: 1000, background: "rgba(0,0,0,0.5)",
               display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}
      onMouseDown={(ev) => { if (ev.target === ev.currentTarget) onClose() }}
    >
      <div
        style={{
          background: "var(--bg-card)", borderRadius: 12, boxShadow: "0 20px 60px rgba(0,0,0,.25)",
          width: "100%", maxWidth: 640, maxHeight: "85vh", overflow: "hidden",
          display: "flex", flexDirection: "column",
        }}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-3 border-b flex items-center justify-between">
          <h2 className="text-[15px] font-semibold">HERO-Verknüpfung</h2>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="overflow-y-auto p-5 space-y-5">
          {err && (
            <div className="p-2.5 rounded-md bg-destructive/10 text-destructive text-[12px]">
              {err}
            </div>
          )}

          {/* Aktueller Kontakt */}
          {currentContactId && !selectedContact && (
            <div
              className="p-3 rounded-md"
              style={
                contactExists.data && contactExists.data.exists === false
                  ? { background: "rgba(239,68,68,0.08)", border: "1px solid rgba(239,68,68,0.35)" }
                  : { background: "rgba(99,102,241,0.05)", border: "1px solid rgba(99,102,241,0.2)" }
              }
            >
              <div className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold mb-1">
                {contactExists.data && contactExists.data.exists === false ? "Verwaiste Verknüpfung" : "Aktuell verknüpft"}
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-[14px] font-medium flex items-center gap-1.5">
                    <User className="w-3.5 h-3.5" />
                    {currentContactName || `Kontakt #${currentContactId}`}
                    {contactExists.data?.exists && contactExists.data.name && contactExists.data.name !== currentContactName && (
                      <span className="text-[11.5px] text-muted-foreground">(HERO: {contactExists.data.name})</span>
                    )}
                  </div>
                  {contactExists.data && contactExists.data.exists === false && (
                    <div className="text-[12px] mt-1" style={{ color: "#dc2626" }}>
                      ⚠ Kontakt #{currentContactId} existiert in HERO nicht mehr. Bitte trennen und neu verknüpfen.
                    </div>
                  )}
                  {currentProjectId && (
                    <div className="text-[12px] text-muted-foreground mt-0.5 flex items-center gap-1.5">
                      <Briefcase className="w-3 h-3" />
                      <HeroVorgangName
                        tenantSlug={tenantSlug}
                        projectId={currentProjectId}
                        nr={verknuepfterVorgang?.code}
                        titel={verknuepfterVorgang?.name}
                      />
                    </div>
                  )}
                </div>
                <button
                  onClick={() => clearMut.mutate()}
                  disabled={clearMut.isPending}
                  className="text-[11.5px] text-destructive hover:underline"
                >
                  Verknüpfung trennen
                </button>
              </div>

              {/* Ansprechpartner-Hinweis: Anrufer ist Ansprechpartner eines Kunden */}
              {existingProjects.data?.parent && (
                <div className="mt-3 p-2.5 rounded-md border border-dashed border-primary/40 bg-primary/5 text-[12px]">
                  {existingProjects.data.parent.ansprechpartner_name || "Anrufer"}
                  {" ist Ansprechpartner von "}
                  <strong>{existingProjects.data.parent.name}</strong>
                  {" — unten dessen Projekte."}
                </div>
              )}

              {/* Bestehende Projekte des Kontakts (bzw. des Eltern-Kunden) */}
              {existingProjects.data?.projects && existingProjects.data.projects.length > 0 && (
                <div className="mt-3 space-y-1.5">
                  <div className="text-[11px] text-muted-foreground">
                    {existingProjects.data?.parent
                      ? `Projekte von ${existingProjects.data.parent.name}:`
                      : "Projekte des Kontakts:"}
                  </div>
                  {existingProjects.data.projects.map((p) => (
                    <button
                      key={p.id}
                      onClick={() => assignProjectMut.mutate(p.id)}
                      disabled={assignProjectMut.isPending || String(p.id) === String(currentProjectId)}
                      className={cn(
                        "w-full text-left px-2.5 py-1.5 rounded border text-[12.5px] flex items-center gap-2 hover:border-primary",
                        String(p.id) === String(currentProjectId) && "bg-primary/10 border-primary",
                      )}
                    >
                      <Briefcase className="w-3 h-3 text-muted-foreground" />
                      <span className="flex-1 truncate">{vorgangBezeichnung(p.code, p.name) ?? VORGANG_OHNE_NAME}</span>
                      {p.status && <span className="text-[10.5px] text-muted-foreground">{p.status}</span>}
                      {String(p.id) === String(currentProjectId) && (
                        <Check className="w-3.5 h-3.5 text-primary" />
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Kontakt-Suche */}
          <div>
            <div className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold mb-1.5">
              {currentContactId ? "Anderen Kontakt wählen" : "Kontakt in HERO suchen"}
            </div>
            <form
              onSubmit={(ev) => { ev.preventDefault(); setErr(null); doContactSearch.mutate() }}
              style={{ display: "flex", gap: 8 }}
            >
              <div style={{ position: "relative", flex: 1 }}>
                <Search
                  className="w-3.5 h-3.5"
                  style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: "var(--text-muted)", pointerEvents: "none" }}
                />
                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Name oder E-Mail..."
                  style={{ width: "100%", paddingLeft: 32 }}
                />
              </div>
              <button
                type="submit"
                disabled={doContactSearch.isPending || !search.trim()}
                className="pp-btn pp-btn-primary pp-btn-sm"
              >
                {doContactSearch.isPending ? "..." : "Suchen"}
              </button>
            </form>

            {searched && foundContacts.length === 0 && (
              <div className="mt-2 text-[12px] text-muted-foreground">
                Keine Treffer. Kontakt ggf. im alten Portal manuell anlegen.
              </div>
            )}
            {foundContacts.length > 0 && (
              <div className="mt-2 space-y-1.5">
                {foundContacts.map((c) => (
                  <button
                    key={c.id}
                    onClick={() => setContactMut.mutate(c)}
                    disabled={setContactMut.isPending}
                    className="w-full text-left p-2.5 rounded-md border hover:border-primary hover:bg-primary/5 transition"
                  >
                    <div className="text-[13.5px] font-medium">
                      {kontaktName(c)}
                    </div>
                    <div className="text-[11.5px] text-muted-foreground">
                      {[c.email, c.phone, c.address].filter(Boolean).join(" · ")}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Projekt-Suche (freitextbasiert, auch wenn noch kein Kontakt) */}
          <div>
            <div className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold mb-1.5">
              Projekt in HERO suchen
            </div>
            <form
              onSubmit={(ev) => { ev.preventDefault(); setErr(null); doProjectSearch.mutate() }}
              style={{ display: "flex", gap: 8 }}
            >
              <div style={{ position: "relative", flex: 1 }}>
                <Briefcase
                  className="w-3.5 h-3.5"
                  style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: "var(--text-muted)", pointerEvents: "none" }}
                />
                <input
                  value={projectSearch}
                  onChange={(e) => setProjectSearch(e.target.value)}
                  placeholder="Projektname / Nummer..."
                  style={{ width: "100%", paddingLeft: 32 }}
                />
              </div>
              <button
                type="submit"
                disabled={doProjectSearch.isPending || !projectSearch.trim()}
                className="pp-btn pp-btn-sm"
              >
                {doProjectSearch.isPending ? "..." : "Suchen"}
              </button>
            </form>
            {foundProjects.length > 0 && (
              <div className="mt-2 space-y-1.5">
                {foundProjects.map((p) => (
                  <button
                    key={p.id}
                    onClick={() => assignProjectMut.mutate(p.id)}
                    disabled={assignProjectMut.isPending}
                    className="w-full text-left p-2.5 rounded-md border hover:border-primary hover:bg-primary/5"
                  >
                    <div className="text-[13px] font-medium flex items-center gap-2">
                      <Briefcase className="w-3 h-3" /> {p.name || `#${p.id}`}
                    </div>
                    <div className="text-[11.5px] text-muted-foreground">
                      {[p.contact_name, p.contact_email, p.status, p.code].filter(Boolean).join(" · ")}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

        </div>

        <div className="px-5 py-3 border-t flex justify-end">
          <button
            onClick={onClose}
            className="text-[12.5px] font-medium px-3 py-1.5 rounded-md bg-secondary hover:bg-secondary/70"
          >
            Schließen
          </button>
        </div>
      </div>
    </div>
  )
}

export function HeroLinkButton({
  onClick,
  linked,
  contactName,
}: {
  onClick: () => void
  linked: boolean
  contactName?: string | null
}) {
  return (
    <button
      onClick={onClick}
      className={cn(
        "inline-flex items-center gap-1.5 text-[12px] font-medium px-3 py-1.5 rounded-md transition",
        linked
          ? "bg-success/10 text-success hover:bg-success/20"
          : "bg-primary/10 text-primary hover:bg-primary/20",
      )}
    >
      {linked ? <Check className="w-3.5 h-3.5" /> : <RotateCw className="w-3.5 h-3.5" />}
      {linked && contactName ? `HERO: ${contactName}` : linked ? "HERO verknüpft" : "HERO verknüpfen"}
    </button>
  )
}
